import type { ISubscription } from "@features/shared/entities/subscription";
import { formatMinorINRRule, getCurrencySymbol } from "@features/helpers";

// What restoring a cancelled subscription does, which the confirmation screen
// (T09) has to say before anything is sent:
//  - no_charge: the paid period still runs. The restore only switches
//    auto-renewal back on; the next charge stays on the regular renewal date.
//  - charge_now: the paid period is over (cancelled while in redemption, or the
//    period ended after the cancel). Solidgate charges the renewal the moment the
//    restore lands.
export type RestoreState = "no_charge" | "charge_now";

export const getRestoreState = (
    subscription: Pick<ISubscription, "endDate">,
    now: number = Date.now()
): RestoreState => (new Date(subscription.endDate).getTime() > now ? "no_charge" : "charge_now");

// GET /subscription/restore-preview: the backend asks Solidgate what the
// restore would do. The amount is the renewal price in minor units — the local
// row only remembers the last charge, which for a trial is the intro price.
// "active" means nothing is cancelled (a restore webhook beat the click).
// source "local" means Solidgate did not answer and the row stood in.
export type RestorePreview = {
    state: RestoreState | "active";
    amount: number;
    currency: string;
    renewsAt?: string | null;
    paymentType?: string | null;
    source: "solidgate" | "local";
};

// What the confirmation screen says: the state, the price (minor units in its
// currency) and the regular renewal date.
export type RestoreTerms = { state: RestoreState; price: number; currency: string; renewsAt: string };

// The terms from the preview when it answered, from the local row otherwise. A
// preview without an amount keeps the local price (the backend sends 0 when
// Solidgate knows no product); "active" reads as no_charge — restoring a
// renewing subscription changes nothing and charges nothing.
export const restoreTermsOf = (
    subscription: Pick<ISubscription, "endDate" | "price" | "currency">,
    preview: RestorePreview | null,
    now: number = Date.now()
): RestoreTerms => {
    if (!preview) {
        return {
            state: getRestoreState(subscription, now),
            price: subscription.price,
            currency: subscription.currency,
            renewsAt: subscription.endDate
        };
    }

    const priced = preview.amount > 0 && !!preview.currency;

    return {
        state: preview.state === "charge_now" ? "charge_now" : "no_charge",
        price: priced ? preview.amount : subscription.price,
        currency: priced ? preview.currency : subscription.currency,
        renewsAt: preview.renewsAt || subscription.endDate
    };
};

// The renewal amount in the subscription's own currency, as the subscriptions
// screen shows prices: price is in minor units, the symbol follows the purchase
// country (US$ vs $), INR drops the ".00". An unknown currency keeps its code.
export const formatRestorePrice = (
    subscription: Pick<ISubscription, "price" | "currency" | "country">,
    lang?: string
): string => {
    const currency = (subscription.currency || "").trim().toUpperCase();
    const amount = formatMinorINRRule(subscription.price / 100, currency);
    const symbol = getCurrencySymbol(currency, lang, subscription.country);

    return symbol ? `${symbol}${amount}` : `${amount} ${currency}`.trim();
};
